import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import styles from './TournamentDetail.module.scss';
import { useGetTournament } from '../../hooks/useGetTournament';
import { useGetActions } from '../../hooks/useGetActions';
import { Loader } from '../Shared/Loader/Loader';
import { TournamentChallengesTable } from './components/TournamentChallengesTable/TournamentChallengesTable';
import { format } from 'date-fns';
import { DATE_FORMAT, QueryKeys } from '../../constants';
import { PopupConfirmDialog } from '../Shared/PopupConfirmDialog/PopupConfirmDialog';
import { useDeleteTournament } from '../../hooks/useDeleteTournament';
import { getStatusColor } from '../../api/tournament';
import { useUpdateTournamentStatus } from '../../hooks/useUpdateTournamentStatus';
import { getAvailableTransitions, StatusAction } from '../../api/tournamentStatusTransitions';

export const TournamentDetail = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [statusDialog, setStatusDialog] = useState<{ visible: boolean; action?: StatusAction; message?: string }>({ visible: false });
    const [statusError, setStatusError] = useState<string | null>(null);
    const { data: tournament, isLoading, refetch } = useGetTournament(Number(id));
    const { data: actions, isLoading: isLoadingActions } = useGetActions();
    const { mutate: deleteTournament } = useDeleteTournament();
    const { mutate: updateStatus, isLoading: isUpdatingStatus } = useUpdateTournamentStatus();

    const onDeleteTournament = () => {
        if (!id) return;
        deleteTournament(Number(id), {
            onSuccess: () => {
                setIsDeleteModalOpen(false);
                queryClient.invalidateQueries(QueryKeys.tournamentsList);
                navigate('/tournaments');
            },
        });
    };

    const handleStatusSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const transition = availableTransitions.find(t => t.action === e.target.value);
        if (!transition) return;
        e.target.value = '';
        setStatusDialog({ visible: true, action: transition.action as StatusAction, message: transition.confirmMessage });
    };

    const handleConfirmStatusChange = () => {
        if (!statusDialog.action || !id) return;
        updateStatus({ id: Number(id), action: statusDialog.action }, {
            onSuccess: () => {
                setStatusDialog({ visible: false });
                queryClient.invalidateQueries([QueryKeys.tournament, Number(id)]);
            },
            onError: (error: any) => {
                setStatusError(error?.response?.data?.detail ?? error?.message ?? 'Failed to update status');
                setStatusDialog({ visible: false });
            },
        });
    };

    if (isLoading || isLoadingActions) {
        return <Loader />;
    }

    if (!tournament) {
        return <div className={styles.empty}>Tournament not found</div>;
    }

    const availableTransitions = getAvailableTransitions(tournament.status as any);

    return (
        <div className={styles.container}>
            <PopupConfirmDialog
                title="Delete Tournament"
                description={`Are you sure you want to delete tournament "${tournament.name}"?`}
                show={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={onDeleteTournament}
                cancelLabel="Cancel"
                confirmLabel="Delete"
                confirmVariant="danger"
            />

            <PopupConfirmDialog
                title="Change Status"
                description={statusDialog.message || ''}
                show={statusDialog.visible}
                onClose={() => setStatusDialog({ visible: false })}
                onConfirm={handleConfirmStatusChange}
                confirmVariant="danger"
            />

            <div className={styles.header}>
                <div className={styles.backButton} onClick={() => navigate('/tournaments')}>← Back</div>
                <div className={styles.title}>{tournament.name}</div>
                <a
                    className={styles.publicLink}
                    href={`/account/tournaments/${id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Public page ↗
                </a>
                {availableTransitions.length > 0 && (
                    <select
                        className={styles.statusSelect}
                        defaultValue=""
                        onChange={handleStatusSelect}
                        disabled={isUpdatingStatus}
                    >
                        <option value="" disabled>Change status</option>
                        {availableTransitions.map(t => (
                            <option key={t.action} value={t.action}>{t.label}</option>
                        ))}
                    </select>
                )}
                <div className={styles.editButton} onClick={() => navigate(`/tournaments/${id}/edit`)}>
                    Edit tournament
                </div>
                <div className={styles.deleteButton} onClick={() => setIsDeleteModalOpen(true)}>
                    Delete tournament
                </div>
            </div>

            {statusError && <div className={styles.errorBanner}>{statusError}</div>}

            <div className={styles.infoCard}>
                <div className={styles.infoGrid}>
                    <div className={styles.infoItem}>
                        <div className={styles.label}>ID</div>
                        <div className={styles.value}>{tournament.id}</div>
                    </div>
                    <div className={styles.infoItem}>
                        <div className={styles.label}>Game</div>
                        <div className={styles.value}>{tournament.game}</div>
                    </div>
                    <div className={styles.infoItem}>
                        <div className={styles.label}>Game Version</div>
                        <div className={styles.value}>{tournament.gameVersion}</div>
                    </div>
                    <div className={styles.infoItem}>
                        <div className={styles.label}>Mode</div>
                        <div className={styles.value}>{tournament.mode || '—'}</div>
                    </div>
                    <div className={styles.infoItem}>
                        <div className={styles.label}>Entry Fee</div>
                        <div className={styles.value}>{tournament.bet}</div>
                    </div>
                    <div className={styles.infoItem}>
                        <div className={styles.label}>Participants</div>
                        <div className={styles.value}>{tournament.participantsAmount} / {tournament.totalParticipants}</div>
                    </div>
                    <div className={styles.infoItem}>
                        <div className={styles.label}>Start Date</div>
                        <div className={styles.value}>{format(new Date(tournament.startDate), DATE_FORMAT)}</div>
                    </div>
                    <div className={styles.infoItem}>
                        <div className={styles.label}>End Date</div>
                        <div className={styles.value}>{format(new Date(tournament.endDate), DATE_FORMAT)}</div>
                    </div>
                    <div className={styles.infoItem}>
                        <div className={styles.label}>Created At</div>
                        <div className={styles.value}>{format(new Date(tournament.createdAt), DATE_FORMAT)}</div>
                    </div>
                    <div className={`${styles.infoItem} ${styles.statusItem}`}>
                        <div className={styles.label}>Status</div>
                        <div className={styles.statusBadge} style={{ backgroundColor: getStatusColor(tournament.status).bg, color: getStatusColor(tournament.status).text, padding: '6px 12px', borderRadius: '6px', display: 'inline-block', fontSize: '14px', fontWeight: '600' }}>
                            {tournament.status}
                        </div>
                    </div>
                    <div className={styles.infoItem}>
                        <div className={styles.label}>Prizes</div>
                        <div className={styles.value}>{tournament.prizes?.join(', ') || '—'}</div>
                    </div>
                </div>
            </div>

            <div className={styles.sectionTitle}>Challenges ({tournament.challenges?.length ?? 0})</div>
            <TournamentChallengesTable
                challenges={tournament.challenges ?? []}
                onChallengeClick={(challengeId) => navigate(`/tournaments/${id}/challenges/${encodeURIComponent(challengeId)}`)}
                actions={actions}
                refetch={refetch}
            />
        </div>
    );
};
